
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, RefreshCw, Loader2 } from 'lucide-react';

interface BulkActionResult {
  gameId: string;
  gameTitle: string;
  success: boolean;
  error?: string;
}

interface BulkActionProgressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: string;
  totalGames: number;
  processedGames: number;
  results: BulkActionResult[];
  isComplete: boolean;
  onRetryFailed: () => void;
}

const BulkActionProgressDialog: React.FC<BulkActionProgressDialogProps> = ({
  open,
  onOpenChange,
  action,
  totalGames,
  processedGames,
  results,
  isComplete,
  onRetryFailed
}) => {
  const successCount = results.filter(r => r.success).length;
  const failedCount = results.filter(r => !r.success).length;
  const progressPercentage = totalGames > 0 ? (processedGames / totalGames) * 100 : 0;
  const failedResults = results.filter(r => !r.success);

  const getActionLabel = (action: string) => {
    const actions: Record<string, string> = {
      'delete': 'Deleting Games',
      'hide': 'Hiding Games',
      'show': 'Showing Games',
      'set-featured': 'Setting Featured',
      'unset-featured': 'Removing Featured',
      'set-trending': 'Setting Trending',
      'unset-trending': 'Removing Trending',
      'set-new': 'Setting New',
      'unset-new': 'Removing New',
      'change-category': 'Changing Category',
      'set-fake-plays': 'Setting Play Count',
      'set-random-rating': 'Setting Random Rating'
    };
    return actions[action] || 'Processing Games';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {!isComplete && <Loader2 className="w-5 h-5 animate-spin" />}
            {getActionLabel(action)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress Section */}
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>{processedGames} / {totalGames}</span>
            </div>
            <Progress value={progressPercentage} className="w-full" />
            <p className="text-sm text-gray-600">
              {isComplete ? 'Process completed' : `Processing ${processedGames} of ${totalGames} games...`}
            </p>
          </div>

          {/* Results Summary */}
          {isComplete && (
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <div className="font-medium text-green-900">Successful</div>
                  <div className="text-sm text-green-700">{successCount} games</div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
                <div>
                  <div className="font-medium text-red-900">Failed</div>
                  <div className="text-sm text-red-700">{failedCount} games</div>
                </div>
              </div>
            </div>
          )}

          {/* Failed Games List */}
          {isComplete && failedResults.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-red-900">Failed Games</h3>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {failedResults.map((result, index) => (
                  <div key={index} className="flex items-start gap-2 p-2 bg-red-50 rounded text-sm">
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium text-red-900">{result.gameTitle}</div>
                      {result.error && (
                        <div className="text-red-700 text-xs">{result.error}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between gap-3 pt-4 border-t">
            <div>
              {isComplete && failedCount > 0 && (
                <Button
                  variant="outline"
                  onClick={onRetryFailed}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retry Failed ({failedCount})
                </Button>
              )}
            </div>
            <Button
              onClick={() => onOpenChange(false)}
              disabled={!isComplete}
            >
              {isComplete ? 'Close' : 'Cancel'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkActionProgressDialog;
